'use client';

import Image from 'next/image';
import { Player } from '@repo/types';
import { Button } from "@/components/ui/button";
import { CirclePlus, RefreshCw, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toMillionPrice } from '../../lib/utils';
import { useCompetitionContext } from '../../providers/CompetitionContext';

interface MatchStat {
  i: number | null;
  m: number;
  r: string | null;
  p: number;
  w: number;
}

interface PlayerActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: Player | null;
  clubName?: string;
  onSwap?: () => void;
  swapLabel?: string;
  onRemove?: () => void;
  onAddToTeam?: () => void;
}

export function PlayerActionModal({
  isOpen,
  onClose,
  driver,
  clubName,
  onSwap,
  swapLabel,
  onRemove,
  onAddToTeam,
}: PlayerActionModalProps) {
  const { competitionData } = useCompetitionContext();

  if (!isOpen || !driver) return null;

  const resolvedClubName = clubName || driver.club?.name || 'Club inconnu';
  const priceInMillions = (toMillionPrice(driver.value) / 1_000_000).toLocaleString('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const matchStats = ((competitionData?.stats || []) as MatchStat[])
    .filter((stat) => stat.i === driver.id)
    .sort((a, b) => a.w - b.w || a.m - b.m);

  const runAction = (action?: () => void) => {
    if (!action) return;
    action();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-xl">{driver.name}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <Image
            src={`https://ocnc-fantasy.b-cdn.net/jerseys/${driver.clubId}.png`}
            alt={driver.name}
            width={56}
            height={56}
            className="w-14 h-14 object-contain"
            onError={(e) => {
              e.currentTarget.src = "https://ocnc-fantasy.b-cdn.net/jerseys/906.png";
            }}
          />
          <div className="space-y-0.5">
            <p className="text-sm text-gray-600">{resolvedClubName}</p>
            <p className="text-sm font-semibold text-[rgb(0_61_96)]">{priceInMillions}M</p>
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-[rgb(0_61_96)]">Résultats par étape</h3>
          {matchStats.length > 0 ? (
            <div className="max-h-56 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Étape</th>
                    <th className="px-3 py-2 text-center font-medium">Place</th>
                    <th className="px-3 py-2 text-right font-medium">Points</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {matchStats.map((stat, index) => (
                    <tr key={`${stat.m}-${stat.w}-${index}`}>
                      <td className="px-3 py-2 font-medium">Étape {stat.w}</td>
                      <td className="px-3 py-2 text-center">
                        {stat.r ? `#${stat.r}` : '-'}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-[rgb(0_61_96)]">
                        {stat.p > 0 ? `+${stat.p}` : stat.p} pts
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-gray-500">
              Aucun résultat disponible.
            </p>
          )}
        </div>

        <div className="grid gap-2">
          {onSwap && (
            <Button type="button" variant="secondary" onClick={() => runAction(onSwap)}>
              <RefreshCw className="h-4 w-4" />
              {swapLabel || 'Permuter'}
            </Button>
          )}
          {onAddToTeam && (
            <Button type="button" variant="green" onClick={() => runAction(onAddToTeam)}>
              <CirclePlus className="h-4 w-4" />
              Ajouter a l'equipe
            </Button>
          )}
          {onRemove && (
            <Button type="button" variant="destructive" onClick={() => runAction(onRemove)}>
              <X className="h-4 w-4" />
              vendre
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
